import {
  Phone,
  Mail,
  Printer,
  MapPin,
  CreditCard,
  ArrowUpRight,
  ChevronUp,
} from "lucide-react";
import { Separator } from "./ui/separator";
import { Button } from "./ui/button";
import { siteData } from "../data/mock";

const legalLinks = [
  { href: "/about.html", label: "About" },
  { href: "/services.html", label: "Services" },
  { href: "/contact.html", label: "Contact Us" },
  { href: "/privacy.html", label: "Privacy Policy" },
  { href: "/terms.html", label: "Terms of Use" },
  { href: "/sms-privacy.html", label: "SMS Privacy Policy" },
  { href: "/sms-terms.html", label: "SMS Terms" },
];

const messageTemplate = `Hi Dr. Kang, I'd like to book an appointment for [Name] at your [Location] office. My preferred time is [Date/Time].`;

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const handleBookAppointment = () => {
    const encodedMessage = encodeURIComponent(messageTemplate);
    window.location.href = `sms:${siteData.clinic.smsNumber}?body=${encodedMessage}`;
  };

  return (
    <footer
      id="contact"
      role="contentinfo"
      className="bg-[#232323] text-stone-300 pt-16 pb-8"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 mb-12">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-2.5 mb-5">
              <div className="w-9 h-9 rounded-lg bg-[#5E8C61] flex items-center justify-center">
                <span className="text-white font-bold text-base tracking-tight">
                  PW
                </span>
              </div>
              <div className="flex flex-col">
                <span className="font-semibold text-white text-sm leading-tight">
                  Pure Wave
                </span>
                <span className="text-[11px] text-[#7DAC7D] leading-tight font-medium">
                  Acupuncture
                </span>
              </div>
            </div>
            <p className="text-sm text-stone-400 leading-relaxed mb-5 max-w-xs">
              Expert acupuncture and pain management by Dr. Jonghan Kang. Serving Orange County with
              precision healing and compassionate care.
            </p>
            <p className="text-xs text-stone-500 mb-5">
              Pure Wave Acupuncture &middot; Pure Wave Medical Group Inc.
            </p>
            <Button
              onClick={handleBookAppointment}
              className="bg-[#5E8C61] hover:bg-[#4A7A4D] text-white text-sm rounded-full h-10 px-5 transition-all duration-200"
              data-testid="footer-book-appointment-btn"
            >
              Book Appointment
              <ArrowUpRight className="ml-1.5 w-3.5 h-3.5" />
            </Button>
            <a
              href={siteData.clinic.intakeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#7DAC7D] hover:text-white transition-colors duration-200"
              data-testid="footer-intake-form-link"
            >
              New Patient Form (fill out before your visit)
              <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" />
            </a>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-semibold text-sm mb-5 uppercase tracking-wider">
              Contact
            </h3>
            <ul className="space-y-3.5" aria-label="Contact information">
              <li>
                <a
                  href="tel:+17146812785"
                  className="flex items-center gap-2.5 text-sm hover:text-[#7DAC7D] transition-colors duration-200"
                >
                  <Phone
                    className="w-4 h-4 text-[#7DAC7D] shrink-0"
                    aria-hidden="true"
                  />
                  {siteData.clinic.phone}
                </a>
              </li>
              <li className="flex items-center gap-2.5 text-sm">
                <Printer
                  className="w-4 h-4 text-[#7DAC7D] shrink-0"
                  aria-hidden="true"
                />
                <span>Fax: {siteData.clinic.fax}</span>
              </li>
              <li>
                <a
                  href={`mailto:${siteData.clinic.email}`}
                  className="flex items-center gap-2.5 text-sm hover:text-[#7DAC7D] transition-colors duration-200"
                >
                  <Mail
                    className="w-4 h-4 text-[#7DAC7D] shrink-0"
                    aria-hidden="true"
                  />
                  {siteData.clinic.email}
                </a>
              </li>
            </ul>
          </div>

          {/* Locations */}
          <div>
            <h3 className="text-white font-semibold text-sm mb-5 uppercase tracking-wider">
              Locations
            </h3>
            <ul className="space-y-3.5" aria-label="Clinic locations">
              {siteData.locations.map((loc) => (
                <li key={loc.id} className="flex items-start gap-2.5 text-sm">
                  <MapPin
                    className="w-4 h-4 text-[#7DAC7D] shrink-0 mt-0.5"
                    aria-hidden="true"
                  />
                  <div>
                    <span className="text-stone-300 font-medium">
                      {loc.name}
                    </span>
                    <span className="text-stone-500 text-xs block mt-0.5">
                      {loc.shortHours}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Insurance & Payment */}
          <div>
            <h3 className="text-white font-semibold text-sm mb-5 uppercase tracking-wider">
              Insurance & Payment
            </h3>
            <ul className="space-y-3" aria-label="Accepted insurance and payment methods">
              {siteData.insurance.map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-2.5 text-sm"
                >
                  <CreditCard
                    className="w-4 h-4 text-[#BCA182] shrink-0"
                    aria-hidden="true"
                  />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Shop */}
          <div>
            <h3 className="text-white font-semibold text-sm mb-5 uppercase tracking-wider">
              Store
            </h3>
            <ul className="space-y-3.5" aria-label="Store links">
              <li>
                <a
                  href={siteData.gongjindan.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 text-sm hover:text-[#BCA182] transition-colors duration-200"
                >
                  <ArrowUpRight
                    className="w-4 h-4 text-[#BCA182] shrink-0"
                    aria-hidden="true"
                  />
                  Gongjindan Online Store
                </a>
              </li>
            </ul>
          </div>
        </div>

        <Separator className="bg-stone-700/30 mb-8" />

        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-stone-500">
          <div className="flex flex-col gap-2 items-center sm:items-start">
            <p className="flex items-center gap-1">
              &copy; {currentYear} Pure Wave Acupuncture &middot; Pure Wave Medical Group Inc. All rights reserved.
            </p>
            <nav
              aria-label="About, legal and policy pages"
              className="flex flex-wrap justify-center sm:justify-start gap-x-4 gap-y-1"
            >
              {legalLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-stone-400 hover:text-[#7DAC7D] transition-colors duration-200"
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </div>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-1.5 text-stone-400 hover:text-[#7DAC7D] transition-colors duration-200 group"
            aria-label="Scroll to top"
            data-testid="scroll-to-top-btn"
          >
            <span>Back to Top</span>
            <ChevronUp className="w-4 h-4 group-hover:-translate-y-0.5 transition-transform duration-200" />
          </button>
        </div>
      </div>
    </footer>
  );
};
